import {
  addBreadcrumb,
  captureException,
  type ErrorEvent,
  type EventHint,
  init,
  setTag,
  setUser,
} from "@sentry/react";
import { BankIdError, isBankIdResponseError } from "#bank-id";
import { ApiResponseError, NetworkError } from "#common";
import type { FixThisAnyLater } from "#types/util";
import { isRelatedToEdgeTranslation } from "./helpers";
import { deepScrub } from "./scrubbing/scrub";

type Options = Parameters<typeof init>[0];

export function initReactSentry(options: Options = {}): void {
  const { beforeSend = (t) => t, ...rest } = options;
  init({
    maxValueLength: 9001,
    beforeSend: async (event, hint) => {
      const result = await beforeSend(event, hint);
      if (!result) {
        return result;
      }
      return beforeSendToSentry(result, hint);
    },
    denyUrls: [
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
      /^safari-extension:/i,
      /^safari-web-extension:/i,
    ],
    ignoreErrors: [
      /**
       * React internal error thrown when something outside react modifies the DOM
       * This is usually because of a browser extension or Chrome's built-in translate
       */
      "NotFoundError: Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node.",
      "NotFoundError: Failed to execute 'insertBefore' on 'Node': The node before which the new node is to be inserted is not a child of this node.",
      "TypeError: undefined is not an object (evaluating 'a.L')",

      // Probably from extension, doesn't seem to affect any user.
      "SecurityError: Failed to read the 'sessionStorage' property from 'Window': Access is denied for this document.",

      // Probably from extension when open a new tab
      // https://sentry.kivra.com/kivra/kivra_inbox/issues/771926
      "TypeError: Cannot read properties of null (reading 'blur')",

      // Probably from extension that wants to change the user agent
      `TypeError: can't redefine non-configurable property "userAgent"`,
      "SyntaxError: Identifier 'change_ua' has already been declared",
      "UnhandledRejection: Non-Error promise rejection captured with value: null",
      "SyntaxError: Failed to execute 'appendChild' on 'Node': Identifier 'change_ua' has already been declared",

      // Probably from google analytics extension
      "TypeError: Cannot redefine property: googletag",

      // Probable network error that creates a lot of noise. If a chunk actually would
      // be missing we would notice it when testing in sandbox
      /^ChunkLoadError/,

      // The PDF from sender is not correct. Nothing we can do about it.
      "Error: pdfErrorInvalidPDFException: Invalid PDF structure.",

      // It is not an actionable error if the user aborted the request.
      /AbortError/,
    ],
    ...rest,
  });
}

export const beforeSendToSentry = (
  event: ErrorEvent,
  hint: EventHint | undefined,
): ErrorEvent | null => {
  const originalException: FixThisAnyLater = hint?.originalException;
  const originalExceptionName: string | undefined = originalException?.name;

  if (originalException instanceof ApiResponseError) {
    event.tags = event.tags || {};

    if (originalException.httpStatusCode === 401) {
      return null;
    }

    if (originalException.correlationId) {
      event.tags.correlationId = originalException.correlationId;
    }
    if (originalException.httpStatusCode) {
      event.tags.httpStatusCode = originalException.httpStatusCode;
    }
    if (ApiResponseError.isServerError(originalException)) {
      event.tags.errorCode = originalException.data.code;
    }

    // We don't wan't to log this error since its because the user has refreshed
    // the page while a bankid request is in progress.
    if (isBankIdResponseError(originalException)) {
      const messageCode = originalException.data.message_code;

      // Codes here: /types/core/bank-id.ts
      const bankIdCodesToIgnore = ["RFA4", "RFA16"];

      if (messageCode && bankIdCodesToIgnore.includes(messageCode)) {
        return null;
      }
    }
  } else if (originalException instanceof NetworkError) {
    // NetworkError happens if the user can't do a network request for some reason.
    // In these cases there is nothing we can do and there is no reason to log this.
    return null;
  } else if (originalException instanceof BankIdError) {
    event.tags = event.tags || {};

    if (originalException.messagesCode) {
      event.message =
        `${originalException.message}: ${originalException.messagesCode}`;
      // Group BankIdErrors by their code (e.g. RFA1, RFA2, etc.)
      event.fingerprint = ["{{ default }}", originalException.messagesCode];
      event.tags.bankIdMessagesCode = originalException.messagesCode;
    }
  } else if (originalExceptionName === "BffResponseError") {
    event.fingerprint = ["BffResponseError"];
  } else if (originalExceptionName === "TinkIframeError") {
    event.fingerprint = ["TinkIframeError"];
  } else if (
    originalException?.name === "TypeError" &&
    originalException?.message === "Session do not exist"
  ) {
    event.fingerprint = ["sessionDoNotExist"];
  } else if (isRelatedToEdgeTranslation(event)) {
    return null;
  }

  // Scrub potentially sensitive information from all events before sending to Sentry
  const scrubbedEvent = deepScrub(event);
  return scrubbedEvent;
};

export function setSentryUser(userKey: string): void {
  setUser({ id: userKey });
}

export {
  addBreadcrumb,
  captureException,
  type ErrorEvent,
  type EventHint,
  setTag,
};
